import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  FormControl,
  Grid,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Field from "components/Field";
import FieldRichText from "components/FieldRichText";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import { useMemo } from "react";
import { useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  AttributeMetaValue,
  SkuAttributeExtended,
  SkuAttributeRequest,
  SkuAttributeByType,
} from "services/KnowfullyAdminClient";
import {
  AttributeCodeConstants,
  AttributeMetaValues as AttributeMetaValueNames,
  SnackbarMessageProps,
} from "types/common-types";
import { SkuFormProps } from "types/sku-related-types";
import translateError from "utils/translateError";
import { withTypes, FieldInputProps, FieldMetaState } from "react-final-form";
import tinymce from "tinymce";
import { SkuAttributeFormComponent } from "./SkuAttributeFormComponent";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { getUtcDate } from "utils/dateUtils";
import { validateSkuAttributeDates } from "utils/validationUtils";
import { SponsorTypeField, sponsorTypeIdToName } from "./SponsorTypeField";

const styles = {
  feedback: {
    width: "100%",
    margin: "20px 0 0 15px",
  },
};

type SkuAttributeUpdateFormProps = {
  skuAttribute?: SkuAttributeExtended;
  skuAttributes: SkuAttributeByType[];
  close: () => void;
  setSnackbarMessage: (snackbarMessage: SnackbarMessageProps | null) => void;
};

type SkuAttributeFormValues = {
  id?: string;
  attributeId?: string;
  organizationAttributeTypeId?: string;
  value: string;
  displayText?: string;
  sequence?: number;
  startDate?: Date | null;
  endDate?: Date | null;
  attributeMetaValues?: AttributeMetaValue[];
  sponsorTypeId?: string;
};

const { Form } = withTypes<SkuAttributeFormValues>();

const SkuAttributeUpdateForm = ({
  skuAttribute,
  skuAttributes,
  sku,
  close,
  setSnackbarMessage,
  onSkuUpdated,
}: SkuAttributeUpdateFormProps & SkuFormProps) => {
  const queryClient = useQueryClient();
  const { formatMessage: t } = useIntl();
  const { skuService } = useGlobalContext();

  const isReusable =
    skuAttribute?.organizationAttributeType?.attributeType?.isReusable;

  const isSponsor =
    skuAttribute?.organizationAttributeType?.attributeType?.codeConstant?.toLowerCase() ===
    AttributeCodeConstants.Sponsors;

  const existingSponsorTypeId = skuAttribute?.skuAttributeMetaValues?.find(
    (m) => m.name === AttributeMetaValueNames.SponsorTypeId,
  )?.value;

  const init = useMemo<SkuAttributeFormValues>(
    () => ({
      id: skuAttribute?.id,
      value: skuAttribute?.value ?? "",
      sequence: skuAttribute?.sequence,
      attributeId: skuAttribute?.attributeId,
      organizationAttributeTypeId: skuAttribute?.organizationAttributeType?.id,
      displayText: skuAttribute?.displayText,
      attributeMetaValues: skuAttribute?.attributeMetaValues ?? [],
      startDate: skuAttribute?.startDate ?? null,
      endDate: skuAttribute?.endDate ?? null,
      sponsorTypeId: existingSponsorTypeId,
    }),
    [skuAttribute, existingSponsorTypeId]
  );

  const validate = (values: SkuAttributeFormValues) => {
    const errors: Partial<Record<keyof SkuAttributeFormValues, string>> = {};

    const dateError = validateSkuAttributeDates(
      skuAttribute?.organizationAttributeType?.attributeType?.allowDates,
      values.startDate,
      values.endDate,
      t({ id: "endDateMustBeAfterStartDate" })
    );

    if (dateError) {
      errors.endDate = dateError;
    }

    return errors;
  };

  const TinyMCEComponent = (
    input: FieldInputProps<any, HTMLElement>,
    meta: FieldMetaState<any>
  ) => {
    return (
      <Grid item xs={12}>
        <FieldRichText
          input={input}
          meta={meta}
          init={{
            height: 280,
          }}
        />
      </Grid>
    );
  };

  const onSubmit = async (values: SkuAttributeFormValues) => {
    let displayText = "";

    if (!isReusable) {
      displayText = tinymce.activeEditor.getContent({ format: "text" });
      values.attributeId = undefined;

      if (!displayText) {
        return { [FORM_ERROR]: t({ id: "skuAttributeEmpty" }) };
      }
    }

    // StartDate to 00:00:00 UTC, EndDate to 23:59:59 UTC
    const transformedValues: SkuAttributeFormValues = {
      ...values,
      startDate: getUtcDate(values.startDate ?? undefined),
      endDate: getUtcDate(values.endDate ?? undefined, {
        endOfDay: true,
      }),
    };

    const skuAttributeMetaValues: AttributeMetaValue[] = isSponsor
      ? [
          AttributeMetaValue.fromJS({
            name: AttributeMetaValueNames.SponsorTypeId,
            value: values.sponsorTypeId ?? "",
          }),
          AttributeMetaValue.fromJS({
            name: AttributeMetaValueNames.SponsorType,
            value: sponsorTypeIdToName(values.sponsorTypeId),
          }),
        ]
      : [];

    try {
      const response = await skuService.saveSkuAttribute(
        sku.id!,
        SkuAttributeRequest.fromJS({
          ...transformedValues,
          displayText,
          skuAttributeMetaValues,
        })
      );

      setSnackbarMessage(
        new SnackbarMessageProps(t({ id: "attributeUpdated" }))
      );
      onSkuUpdated && onSkuUpdated(sku.id!);
      queryClient.invalidateQueries(["attributes"]);
      queryClient.invalidateQueries(["skuAttributes"]);
      queryClient.invalidateQueries(["skuAttributesByType"]);
      close();

      return response;
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  return (
    <Form
      onSubmit={onSubmit}
      validate={validate}
      initialValues={init}
      render={({ handleSubmit, submitting, submitError, values }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent dividers>
              <Grid container spacing={2}>
                {isReusable ? (
                  SkuAttributeFormComponent(
                    skuAttributes,
                    values,
                    skuAttribute?.organizationAttributeType!,
                    skuAttribute?.attributeId
                  )
                ) : (
                  <Field name="value">
                    {({ input, meta }) => TinyMCEComponent(input, meta)}
                  </Field>
                )}
                {isSponsor && (
                  <SponsorTypeField
                    fieldName="sponsorTypeId"
                    initialValue={existingSponsorTypeId}
                  />
                )}

                {skuAttribute?.organizationAttributeType?.attributeType
                  ?.allowDates && (
                  <>
                    <Grid item xs={12}>
                      <Field name="startDate">
                        {({ input, meta }) => (
                          <FormControl
                            fullWidth
                            error={meta.error && meta.submitFailed}
                          >
                            <DatePicker
                              label={t({ id: "startDateLabel" })}
                              format="MM/dd/yyyy"
                              disabled={false}
                              slotProps={{
                                textField: {
                                  variant: "standard",
                                  error: meta.error && meta.submitFailed,
                                  helperText:
                                    meta.error &&
                                    meta.submitFailed &&
                                    meta.error,
                                },
                                actionBar: {
                                  actions: ["clear", "cancel"],
                                },
                              }}
                              {...input}
                              value={input.value === "" ? null : input.value}
                            />
                          </FormControl>
                        )}
                      </Field>
                    </Grid>
                    <Grid item xs={12}>
                      <Field name="endDate">
                        {({ input, meta }) => (
                          <FormControl
                            fullWidth
                            error={meta.error && meta.submitFailed}
                          >
                            <DatePicker
                              label={t({ id: "endDateLabel" })}
                              format="MM/dd/yyyy"
                              disabled={false}
                              slotProps={{
                                textField: {
                                  variant: "standard",
                                  error: meta.error && meta.submitFailed,
                                  helperText:
                                    meta.error &&
                                    meta.submitFailed &&
                                    meta.error,
                                },
                                actionBar: {
                                  actions: ["clear", "cancel"],
                                },
                              }}
                              {...input}
                              value={input.value === "" ? null : input.value}
                            />
                          </FormControl>
                        )}
                      </Field>
                    </Grid>
                  </>
                )}

                {submitError && (
                  <Alert sx={styles.feedback} severity="error">
                    {submitError}
                  </Alert>
                )}
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button disabled={submitting} onClick={close}>
                {t({ id: "cancelButton" })}
              </Button>

              <Button
                type="submit"
                color="primary"
                disabled={submitting || (!values.attributeId && !values.value)}
                startIcon={submitting && <CircularProgress size={16} />}
              >
                {t({ id: "saveButton" })}
              </Button>
            </DialogActions>
          </form>
        )}
      />
    );
};

export default SkuAttributeUpdateForm;
